/**
 * Every call live on the account, kept current over SSE.
 *
 *   GET {operations}/api/voice/live-calls/stream
 *     event: snapshot   the whole list (sent on connect, and again whenever the
 *                       server had to rebuild it — a pub/sub reconnect, say)
 *     event: call       one call changed: call.updated | call.ended | call.removed
 *
 * Same fetch-based SSE mechanics as useInboxStream / useCallTranscriptStream
 * (EventSource cannot send the Authorization header).
 *
 * Reliability, in order of what fails most:
 *  - a dropped connection reconnects with jittered backoff, forever — this is a
 *    wallboard, it should come back on its own after a deploy or a network blip;
 *    a reconnect gets a fresh snapshot, so nothing needs replaying
 *  - while the stream is down, the REST snapshot is polled so the list is never
 *    more than a few seconds stale
 *  - 401/403/404 are terminal: signed out, not permitted, or a calling service
 *    that predates the monitor — retrying would only hammer the proxy
 *  - 503 means the calling service is at its stream cap for this account
 *    (25 of them). It is asking for room, so this backs off by a minute and
 *    does NOT start polling: a poll every few seconds would be the opposite of
 *    what it asked for, from every tab that got turned away at once
 *
 * A tab nobody is looking at holds nothing. Since this now runs on every page
 * rather than only on the Live calls page, a background tab left open all
 * afternoon would otherwise sit on a connection — and a handful of those per
 * person is how an account reaches that cap. Hidden for a minute, it
 * disconnects; shown again, it reconnects and takes a fresh snapshot.
 *
 * The hook never throws.
 */

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useAuthTenantId, useAuthToken, useAuthUserId } from '@/stores/auth.store';
import { useTenantId } from '@/stores/tenant.store';
import { HIDDEN_GRACE_MS, useHiddenTabGrace } from '@/hooks/lib/useHiddenTabGrace';
import { useStreamLease } from '@/hooks/lib/useStreamLease';
import {
  initialLiveCallsState,
  liveCallsApi,
  liveCallsReducer,
  type LiveCall,
  type LiveCallChange,
  type LiveCallsFilters,
  type LiveCallsSnapshot,
  type LiveCallsState,
} from '@/lib/calls/liveCalls';

export type LiveCallsStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'forbidden'
  | 'unavailable'
  | 'error';

export interface UseLiveCallsStreamResult {
  state: LiveCallsState;
  /** Live calls, longest-running first. */
  calls: LiveCall[];
  /** Calls that just ended, still fading out. */
  endedCalls: LiveCall[];
  status: LiveCallsStreamStatus;
  /** True while the list is being kept fresh by polling instead of the stream. */
  polling: boolean;
}

const RECONNECT_BASE_MS = 1_000;
export { HIDDEN_GRACE_MS };
/** The service is at its per-account stream cap: wait, and don't poll. */
const OVER_CAPACITY_RETRY_MS = 60_000;
const RECONNECT_CAP_MS = 30_000;
const POLL_WHILE_DOWN_MS = 5_000;
/** Polling starts after this many failed connects in a row. */
const POLL_AFTER_FAILURES = 2;
const PRUNE_EVERY_MS = 1_000;

function backoffMs(attempt: number): number {
  const raw = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  return Math.round(raw * (0.75 + Math.random() * 0.25));
}

function operationsBaseUrl(): string {
  const url = import.meta.env.VITE_OPERATIONS_URL ?? '';
  return typeof url === 'string' ? url.replace(/\/+$/, '') : '';
}

export function parseSseBlocks(buffer: string): {
  events: { event: string | null; data: string }[];
  rest: string;
} {
  const events: { event: string | null; data: string }[] = [];
  let rest = buffer.replace(/\r\n/g, '\n');
  let boundary: number;
  while ((boundary = rest.indexOf('\n\n')) !== -1) {
    const block = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);
    let event: string | null = null;
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      if (colon === -1) continue;
      const field = line.slice(0, colon);
      const value = line.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length) events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

/**
 * Which auth context has been refused the live-calls stream.
 *
 * Module scope on purpose: the hook remounts on every navigation, and without
 * this each mount re-asked and was refused again, holding one of the six
 * per-origin connections for the duration of the request.
 */
let forbiddenSessionKey: string | null = null;

/** Forget the refusal when explicitly resetting the live-calls session. */
export function resetLiveCallsStreamPermission(): void {
  forbiddenSessionKey = null;
}

export function useLiveCallsStream(
  filters: LiveCallsFilters = {},
  enabled = true,
): UseLiveCallsStreamResult {
  const [state, dispatch] = useReducer(liveCallsReducer, initialLiveCallsState);
  const [status, setStatus] = useState<LiveCallsStreamStatus>('idle');
  const [polling, setPolling] = useState(false);
  const hidden = useHiddenTabGrace();
  const filterKey = JSON.stringify(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // A 403 belongs to the credentials and tenant that received it. A new
  // login, impersonation, tenant switch or refreshed grant must get a try.
  const token = useAuthToken();
  const userId = useAuthUserId();
  const storedTenantId = useTenantId();
  const authTenantId = useAuthTenantId();
  const tenantId = storedTenantId ?? authTenantId;
  const sessionKey = JSON.stringify([tenantId, userId, token]);

  // One tab across the whole browser holds this connection, and only while it
  // is visible. The others poll the snapshot instead of streaming: unlike the
  // inbox and the bell, this list is rebuilt from a snapshot rather than
  // accumulated from events, so a tab that was never listening loses nothing
  // by asking for it on a timer. See `useStreamLease`.
  const wants = enabled && !hidden;
  const live = useStreamLease('live-calls', wants) && wants;

  useEffect(() => {
    dispatch({ type: 'reset' });
    if (!live) {
      setStatus('idle');
      if (!wants) setPolling(false);
      return;
    }
    if (
      typeof window === 'undefined' ||
      typeof window.fetch !== 'function' ||
      typeof TextDecoder !== 'function'
    ) {
      setStatus('error');
      return;
    }

    const abort = new AbortController();
    let cancelled = false;
    let failures = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const set = (next: LiveCallsStreamStatus) => {
      if (!cancelled) setStatus(next);
    };

    const startPolling = () => {
      if (pollTimer || cancelled) return;
      setPolling(true);
      const poll = async () => {
        try {
          const snapshot = await liveCallsApi.list(filtersRef.current);
          if (!cancelled) dispatch({ type: 'snapshot', snapshot, now: Date.now() });
        } catch {
          // The stream reconnect loop is still running; the next poll may work.
        }
      };
      void poll();
      pollTimer = setInterval(poll, POLL_WHILE_DOWN_MS);
    };
    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      if (!cancelled) setPolling(false);
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      failures += 1;
      if (failures >= POLL_AFTER_FAILURES) startPolling();
      set('reconnecting');
      reconnectTimer = setTimeout(connect, backoffMs(failures - 1));
    };

    async function connect(): Promise<void> {
      if (cancelled) return;
      // Already refused this session — do not spend another connection on it.
      // A browser allows only six per origin and a dashboard tab opens three
      // long-lived streams, so two tabs exhaust the pool and every ordinary
      // request behind them queues until one frees. A permission refusal is
      // not transient, so asking again on each navigation costs a slot and
      // buys nothing.
      if (forbiddenSessionKey === sessionKey) {
        set('forbidden');
        return;
      }
      set(failures === 0 ? 'connecting' : 'reconnecting');

      if (!token) {
        set('forbidden');
        return;
      }
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      if (tenantId) headers['x-tenant-id'] = tenantId;

      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filtersRef.current)) {
        if (value) params.set(key, String(value));
      }
      const qs = params.toString();

      try {
        const response = await fetch(
          `${operationsBaseUrl()}${liveCallsApi.streamUrl()}${qs ? `?${qs}` : ''}`,
          { headers, signal: abort.signal, credentials: 'omit', cache: 'no-store' },
        );
        if (response.status === 401 || response.status === 403) {
          stopPolling();
          // Only a 403 is durable. A 401 is an expired token, and the next
          // mount carries a refreshed one.
          if (response.status === 403) forbiddenSessionKey = sessionKey;
          set('forbidden');
          return;
        }
        if (response.status === 404) {
          stopPolling();
          set('unavailable');
          return;
        }
        if (response.status === 503) {
          // At the per-account stream cap. Give it a minute — jittered, so the
          // tabs that were all refused together don't come back together — and
          // do not poll in the meantime.
          stopPolling();
          failures = 0;
          set('reconnecting');
          reconnectTimer = setTimeout(
            connect,
            Math.round(OVER_CAPACITY_RETRY_MS * (0.75 + Math.random() * 0.5)),
          );
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`live calls stream failed: ${response.status}`);
        }

        failures = 0;
        stopPolling();
        set('open');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        try {
          while (!abort.signal.aborted) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parsed = parseSseBlocks(buffer);
            buffer = parsed.rest;
            for (const { event, data } of parsed.events) {
              try {
                if (event === 'snapshot') {
                  dispatch({
                    type: 'snapshot',
                    snapshot: JSON.parse(data) as LiveCallsSnapshot,
                    now: Date.now(),
                  });
                } else if (event === 'call') {
                  dispatch({
                    type: 'change',
                    change: JSON.parse(data) as LiveCallChange,
                    now: Date.now(),
                  });
                }
              } catch {
                // A malformed frame: the next snapshot is authoritative.
              }
            }
          }
        } finally {
          try {
            reader.releaseLock();
          } catch {
            /* already released */
          }
        }
        // The server closes streams on a lifetime cap; that is a reconnect, not a failure.
        if (!cancelled) {
          set('reconnecting');
          reconnectTimer = setTimeout(connect, backoffMs(0));
        }
      } catch {
        if (abort.signal.aborted || cancelled) return;
        scheduleReconnect();
      }
    }

    const pruneTimer = setInterval(
      () => dispatch({ type: 'prune', now: Date.now() }),
      PRUNE_EVERY_MS,
    );
    void connect();

    return () => {
      cancelled = true;
      abort.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pollTimer) clearInterval(pollTimer);
      clearInterval(pruneTimer);
    };
    // filterKey stands in for the filters object, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, wants, filterKey, sessionKey]);

  // A tab that wants live calls but does not hold the lease still has a list to
  // show. Without this it rendered empty until the leader closed — the bubble
  // on every page simply went blank on the second window.
  useEffect(() => {
    if (!wants || live) return;
    let cancelled = false;
    setStatus('reconnecting');
    setPolling(true);
    const poll = async () => {
      try {
        const snapshot = await liveCallsApi.list(filtersRef.current);
        if (!cancelled) dispatch({ type: 'snapshot', snapshot, now: Date.now() });
      } catch {
        // The leader is the authority; the next tick may work.
      }
    };
    void poll();
    const timer = setInterval(poll, POLL_WHILE_DOWN_MS);
    const prune = setInterval(() => dispatch({ type: 'prune', now: Date.now() }), PRUNE_EVERY_MS);
    return () => {
      cancelled = true;
      setPolling(false);
      clearInterval(timer);
      clearInterval(prune);
    };
    // filterKey stands in for the filters object, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wants, live, filterKey]);

  const calls = useMemo(
    () => Object.values(state.calls).sort((a, b) => a.startedAt - b.startedAt),
    [state.calls],
  );
  const endedCalls = useMemo(
    () => Object.values(state.ending).map((e) => ({ ...e.call })),
    [state.ending],
  );

  return { state, calls, endedCalls, status, polling };
}
